import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';

// Async thunks
export const fetchSprints = createAsyncThunk(
  'sprints/fetchSprints',
  async ({ projectId, status }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (status) params.append('status_filter', status);
      const response = await api.get(`/projects/${projectId}/sprints?${params}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch sprints');
    }
  }
);

export const fetchActiveSprint = createAsyncThunk(
  'sprints/fetchActiveSprint',
  async (projectId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/projects/${projectId}/sprints/active`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch active sprint');
    }
  }
);

export const fetchSprintById = createAsyncThunk(
  'sprints/fetchSprintById',
  async ({ projectId, sprintId }, { rejectWithValue }) => {
    try {
      const response = await api.get(`/projects/${projectId}/sprints/${sprintId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch sprint');
    }
  }
);

export const createSprint = createAsyncThunk(
  'sprints/createSprint',
  async ({ projectId, sprintData }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/projects/${projectId}/sprints`, sprintData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to create sprint');
    }
  }
);

export const updateSprint = createAsyncThunk(
  'sprints/updateSprint',
  async ({ projectId, sprintId, sprintData }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/projects/${projectId}/sprints/${sprintId}`, sprintData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to update sprint');
    }
  }
);

export const startSprint = createAsyncThunk(
  'sprints/startSprint',
  async ({ projectId, sprintId, startData = {} }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/projects/${projectId}/sprints/${sprintId}/start`, startData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to start sprint');
    }
  }
);

export const completeSprint = createAsyncThunk(
  'sprints/completeSprint',
  async ({ projectId, sprintId, completeData = {} }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/projects/${projectId}/sprints/${sprintId}/complete`, completeData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to complete sprint');
    }
  }
);

export const deleteSprint = createAsyncThunk(
  'sprints/deleteSprint',
  async ({ projectId, sprintId, force = false }, { rejectWithValue }) => {
    try {
      await api.delete(`/projects/${projectId}/sprints/${sprintId}`, {
        params: { force }
      });
      return sprintId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to delete sprint');
    }
  }
);

export const assignTasksToSprint = createAsyncThunk(
  'sprints/assignTasksToSprint',
  async ({ projectId, sprintId, taskIds }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/projects/${projectId}/sprints/${sprintId}/tasks`, { task_ids: taskIds });
      return { sprintId, tasks: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to assign tasks to sprint');
    }
  }
);

export const removeTasksFromSprint = createAsyncThunk(
  'sprints/removeTasksFromSprint',
  async ({ projectId, taskIds }, { rejectWithValue }) => {
    try {
      const response = await api.delete(`/projects/${projectId}/sprints/tasks`, { 
        data: { task_ids: taskIds } 
      });
      return { tasks: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to remove tasks from sprint');
    }
  }
);

const initialState = {
  sprints: [],
  activeSprint: null,
  currentSprint: null,
  loading: false,
  error: null,
  total: 0,
  page: 1,
  pageSize: 50,
  totalPages: 1,
};

const sprintsSlice = createSlice({
  name: 'sprints',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentSprint: (state, action) => {
      state.currentSprint = action.payload;
    },
    clearSprints: (state) => {
      state.sprints = [];
      state.activeSprint = null;
      state.currentSprint = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch sprints
      .addCase(fetchSprints.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSprints.fulfilled, (state, action) => {
        state.loading = false;
        state.sprints = action.payload.items;
        state.total = action.payload.total;
        state.page = action.payload.page;
        state.pageSize = action.payload.page_size;
        state.totalPages = action.payload.total_pages;
        // Extract and set active sprint from the fetched list
        state.activeSprint = action.payload.items.find(sprint => sprint.status === 'active') || null;
      })
      .addCase(fetchSprints.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch active sprint
      .addCase(fetchActiveSprint.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchActiveSprint.fulfilled, (state, action) => {
        state.loading = false;
        state.activeSprint = action.payload;
      })
      .addCase(fetchActiveSprint.rejected, (state, action) => {
        state.loading = false;
        state.activeSprint = null;
      })
      
      // Fetch sprint by ID
      .addCase(fetchSprintById.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSprintById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentSprint = action.payload;
      })
      .addCase(fetchSprintById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Create sprint
      .addCase(createSprint.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createSprint.fulfilled, (state, action) => {
        state.loading = false;
        state.sprints.push(action.payload);
      })
      .addCase(createSprint.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Update sprint
      .addCase(updateSprint.fulfilled, (state, action) => {
        const index = state.sprints.findIndex(s => s.id === action.payload.id);
        if (index !== -1) {
          state.sprints[index] = action.payload;
        }
        if (state.activeSprint?.id === action.payload.id) {
          state.activeSprint = action.payload;
        }
        if (state.currentSprint?.id === action.payload.id) {
          state.currentSprint = action.payload;
        }
      })
      
      // Start sprint
      .addCase(startSprint.fulfilled, (state, action) => {
        const index = state.sprints.findIndex(s => s.id === action.payload.id);
        if (index !== -1) {
          state.sprints[index] = action.payload;
        }
        state.activeSprint = action.payload;
      })
      .addCase(startSprint.rejected, (state, action) => {
        state.error = action.payload;
      })
      
      // Complete sprint
      .addCase(completeSprint.fulfilled, (state, action) => {
        const index = state.sprints.findIndex(s => s.id === action.payload.id);
        if (index !== -1) {
          state.sprints[index] = action.payload;
        }
        if (state.activeSprint?.id === action.payload.id) {
          state.activeSprint = null;
        }
      })
      .addCase(completeSprint.rejected, (state, action) => {
        state.error = action.payload;
      })
      
      // Delete sprint
      .addCase(deleteSprint.fulfilled, (state, action) => {
        state.sprints = state.sprints.filter(s => s.id !== action.payload);
        if (state.activeSprint?.id === action.payload) {
          state.activeSprint = null;
        }
        if (state.currentSprint?.id === action.payload) {
          state.currentSprint = null;
        }
      });
  },
});

export const { clearError, setCurrentSprint, clearSprints } = sprintsSlice.actions;
export default sprintsSlice.reducer;
